/**
 * Per-account trades history — full lifecycle ledger.
 *
 * Sits under AccountLivePositions in the Live Account Stats card on
 * `/pairs/:slug` and gives the operator a flat record of every trade
 * the dashboard has seen on this leg — open or closed — newest first.
 * The live positions card above is the realtime view; this is the
 * permanent record from D1 with a per-row `Open` / `Closed` tag, so
 * the operator can audit the full history without leaving the pair
 * page even when nothing has closed yet.
 *
 * Pulls `GET /api/accounts/:id/trades` (worker route at
 * `apps/worker/src/routes/api/accounts.ts:892`) which returns up to
 * 200 rows from the `trades` table.
 *
 * No WS subscription — this is a static snapshot fetched on mount
 * and refreshed when the parent re-renders the leg block. Live
 * opens / closes land on the next refetch (account-pair detail
 * reloads on focus).
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getAccountTrades, type AccountTrade } from "@/lib/api/accounts";
import { useDashboardStore } from "@/store/dashboard.store";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";

interface Props {
  /** Opaque slug or raw id — passed straight to the trades route. */
  accountId: string;
  /** Raw account id (NOT slug) — used to match the dashboard store's
   *  `pair.positions` keys, which are keyed by raw id. Pass this when
   *  you want open trades to show live open PnL from the WS slice;
   *  omit and they'll show "—" until close. */
  rawAccountId?: string;
  /** Symbol-pair ids whose live position slices should be searched
   *  for the live open PnL. Same input shape as AccountLivePositions.
   *  Required for the live-PnL overlay to fire. */
  symbolPairIds?: ReadonlyArray<string>;
  className?: string;
  /** Cap how many rows to render. Defaults to 30 — older entries are
   *  available on the dedicated AccountDetail page. */
  limit?: number;
}

export function AccountTradesHistory({
  accountId,
  rawAccountId,
  symbolPairIds,
  className,
  limit = 30,
}: Props): ReactNode {
  const [trades, setTrades] = useState<AccountTrade[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pairBuckets = useDashboardStore((s) => s.pairs);

  // Build a ticket → live openPnl map from every relevant pair slice.
  // Open trades fall through to this when their persisted `pnl` is
  // null (which is the canonical state pre-close), so the operator
  // sees the same live PnL the live-positions card is rendering.
  const liveOpenPnlByTicket = useMemo(() => {
    const out = new Map<number, number>();
    if (!rawAccountId || !symbolPairIds || symbolPairIds.length === 0) return out;
    for (const pid of symbolPairIds) {
      const positions = pairBuckets[pid]?.positions;
      if (!positions) continue;
      for (const p of Object.values(positions)) {
        if (p.accountId !== rawAccountId) continue;
        const prev = out.get(p.ticket);
        // Multiple slices can fan the same ticket — keep the freshest
        // by openPnl write timing. We don't have updatedAt context per
        // entry here, but Object.values iteration order is insertion;
        // last-wins is acceptable since both copies are seconds apart.
        if (prev == null || prev !== p.openPnl) out.set(p.ticket, p.openPnl);
      }
    }
    return out;
  }, [pairBuckets, rawAccountId, symbolPairIds]);

  // Do we have a *trustworthy* live snapshot for this account? True
  // only when the bot has reported a tick on this account in the
  // last 2 minutes (online=true + fresh updatedAt). The dashboard's
  // own WS-to-worker `connectionStatus` is NOT enough — that stays
  // "open" even when the bot is asleep, so it would falsely declare
  // every quiet weekend row "stale". A closed_at IS NULL row that
  // isn't in liveOpenPnlByTicket AND hasLiveSnapshot=true means the
  // broker has actually closed the trade but D1 hasn't reconciled
  // yet (history-fetch cron runs only while a bot is online).
  const FRESH_TICK_MS = 2 * 60_000;
  const hasLiveSnapshot = useMemo(() => {
    if (!rawAccountId || !symbolPairIds || symbolPairIds.length === 0) return false;
    const cutoff = Date.now() - FRESH_TICK_MS;
    for (const pid of symbolPairIds) {
      const acc = pairBuckets[pid]?.accounts[rawAccountId];
      if (acc != null && acc.online === true && acc.updatedAt >= cutoff) {
        return true;
      }
    }
    return false;
  }, [pairBuckets, rawAccountId, symbolPairIds]);

  const loadTrades = useCallback((): (() => void) => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAccountTrades(accountId)
      .then((rows) => {
        if (cancelled) return;
        setTrades(rows);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setTrades([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => loadTrades(), [loadTrades]);

  const rows = useMemo(() => {
    if (!trades) return [];
    return trades.slice(0, limit);
  }, [trades, limit]);

  const counts = useMemo(() => {
    if (!trades) return { open: 0, stale: 0, closed: 0 };
    let open = 0;
    let stale = 0;
    let closed = 0;
    for (const t of trades) {
      if (t.closed_at != null) {
        closed++;
        continue;
      }
      // closed_at IS NULL: open *if* live snapshot confirms it; else
      // stale (broker likely closed; D1 not reconciled). When we have
      // no live snapshot at all (bot offline), default to "open" — we
      // can't prove staleness without the observer.
      if (!hasLiveSnapshot || liveOpenPnlByTicket.has(t.ticket)) open++;
      else stale++;
    }
    return { open, stale, closed };
  }, [trades, hasLiveSnapshot, liveOpenPnlByTicket]);

  return (
    <div
      className={cn(
        "min-w-0 overflow-hidden rounded-xl border border-border/40 bg-card/40 p-3 backdrop-blur-sm sm:p-4",
        className,
      )}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-sans text-sm font-medium text-foreground">
          Trades History
        </h3>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {loading
            ? "Loading…"
            : counts.open + counts.stale + counts.closed === 0
              ? "Empty"
              : [
                  counts.open > 0 ? `${counts.open} open` : null,
                  counts.stale > 0 ? `${counts.stale} stale` : null,
                  counts.closed > 0 ? `${counts.closed} closed` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
        </span>
      </div>

      <AnimatePresence mode="wait">
        {error ? (
          <motion.div
            key="err"
            role="alert"
            aria-live="polite"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11px] text-destructive"
          >
            <span>Failed to load trades: {error}</span>
            <Button size="sm" variant="outline" onClick={loadTrades}>
              Retry
            </Button>
          </motion.div>
        ) : rows.length === 0 && !loading ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/40 py-6 text-center"
          >
            <p className="text-xs font-medium text-foreground">No trades yet</p>
            <p className="mt-1 text-[10px] text-muted-foreground/70">
              History fills in as positions open + close on this account.
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="rows"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            className="overflow-x-auto"
          >
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-1.5 py-1 font-medium">Ticket</th>
                  <th className="px-1.5 py-1 font-medium">Symbol</th>
                  <th className="px-1.5 py-1 font-medium">Side</th>
                  <th className="px-1.5 py-1 text-right font-medium">Lots</th>
                  <th className="px-1.5 py-1 text-right font-medium">Entry</th>
                  <th className="px-1.5 py-1 text-right font-medium">Close</th>
                  <th className="px-1.5 py-1 text-right font-medium">PnL</th>
                  <th className="px-1.5 py-1 text-right font-medium">Held</th>
                  <th className="px-1.5 py-1 text-right font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const dbOpen = t.closed_at == null;
                  // A `closed_at IS NULL` row is only *really* open
                  // when (a) we have no live snapshot to refute it
                  // (bot offline → trust DB) or (b) the live snapshot
                  // contains the ticket. Otherwise it's stale: broker
                  // closed it and the bot's history-fetch reconciler
                  // hasn't run yet (e.g. weekend, mid-sleep window).
                  const isLiveOpen =
                    dbOpen &&
                    (!hasLiveSnapshot || liveOpenPnlByTicket.has(t.ticket));
                  const isStale = dbOpen && !isLiveOpen;
                  const heldMs = dbOpen
                    ? Date.now() - t.opened_at
                    : (t.closed_at ?? 0) - t.opened_at;
                  // Live PnL is meaningful only while the WS still
                  // reports the ticket. Stale rows fall through to
                  // the persisted (null) pnl and render as "—".
                  const livePnl = isLiveOpen
                    ? liveOpenPnlByTicket.get(t.ticket) ?? null
                    : null;
                  const displayPnl = livePnl ?? t.pnl;
                  return (
                    <tr
                      key={t.id}
                      className={cn(
                        "border-t border-border/30 transition-colors",
                        isLiveOpen && "bg-accent/5",
                        isStale && "bg-amber-500/5 opacity-70",
                      )}
                    >
                      <td className="px-1.5 py-1.5 font-mono tabular-nums text-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          {t.ticket}
                          {isLiveOpen && (
                            <Badge
                              variant="outline"
                              className="border-accent/40 px-1 py-0 text-[9px] uppercase text-accent"
                              title="This trade is live in the broker's positions feed."
                            >
                              Open
                            </Badge>
                          )}
                          {isStale && (
                            <Badge
                              variant="warning"
                              className="px-1 py-0 text-[9px] uppercase"
                              title="Broker no longer reports this ticket as open. The bot will reconcile the close price + PnL on its next history fetch."
                            >
                              Stale
                            </Badge>
                          )}
                        </span>
                      </td>
                      <td className="px-1.5 py-1.5 font-medium text-foreground">
                        {t.symbol}
                      </td>
                      <td
                        className={cn(
                          "px-1.5 py-1.5 font-medium uppercase",
                          t.side === "buy" ? "text-emerald-500" : "text-rose-500",
                        )}
                      >
                        {t.side}
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-mono tabular-nums">
                        {t.lots}
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-mono tabular-nums">
                        {t.entry_price}
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-mono tabular-nums">
                        {t.close_price ?? "—"}
                      </td>
                      <td
                        className={cn(
                          "px-1.5 py-1.5 text-right font-mono tabular-nums font-medium",
                          displayPnl != null && displayPnl > 0 && "text-emerald-500",
                          displayPnl != null && displayPnl < 0 && "text-rose-500",
                          isLiveOpen && livePnl != null && "italic",
                        )}
                        title={
                          isLiveOpen && livePnl != null
                            ? "Live open PnL from the WS feed (settled value lands on close)."
                            : undefined
                        }
                      >
                        {displayPnl != null ? fmtMoney(displayPnl) : "—"}
                      </td>
                      <td className="px-1.5 py-1.5 text-right text-[10px] text-muted-foreground">
                        {formatDuration(heldMs)}
                      </td>
                      <td className="px-1.5 py-1.5 text-right text-[10px] text-muted-foreground">
                        {dbOpen
                          ? new Date(t.opened_at).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : t.closed_at != null
                            ? new Date(t.closed_at).toLocaleString(undefined, {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}
